import { useMemo } from "react"

import { TickRendererProps } from "@visx/axis"
import { Text } from "@visx/text"

import { useDefaultRateDisclosureQuery } from "@/api/graphql"
import { Link } from "@/components/Anchor"
import { CardBody, CardWrapper } from "@/components/Card"
import { GraphLabel, GraphLabels } from "@/components/graphs/GraphLabels"
import { MultiGraph } from "@/components/graphs/types/bar-line/MultiGraph"
import { Heading } from "@/components/Typography"
import {
	METABASE_FINANCIAL_PERFORMANCE_URL,
	endDateStatistics,
} from "@/constants/metabase"
import { Routes } from "@/constants/routes"
import { useLang } from "@/context/useLang"
import { useLocalizedDateTime } from "@/lib/dates"
import { useTrans } from "@/lib/i18n"
import { useMetabasePublicQuestion } from "@/lib/metabase"

interface FinancialPerformanceRow {
	calendar_year: number
	interest_paid: number | null
	capital_outstanding_start_of_year: number | null
	effective_interest_rate: number | null
}

export function FinancialPerformanceGraph() {
	const DateTime = useLocalizedDateTime()
	const { formatNumber, formatCurrency } = useLang()
	const t = useTrans("dashboard")

	const { rows: allRows, isLoading } =
		useMetabasePublicQuestion<FinancialPerformanceRow>(
			METABASE_FINANCIAL_PERFORMANCE_URL,
		)
	const rows = useMemo(
		() => allRows.filter((row) => row.interest_paid !== null),
		[allRows],
	)

	const { data: defaultRateData } = useDefaultRateDisclosureQuery()
	const defaultRates =
		defaultRateData?.platform_statistics?.default_rates ?? []

	const barData = useMemo(
		() => [
			{
				id: "capital_at_risk",
				variant: "primary" as const,
				data: rows.map((row) => ({
					x: row.calendar_year.toString(),
					y: row.capital_outstanding_start_of_year ?? 0,
				})),
			},
		],
		[rows],
	)

	const lineData = useMemo(
		() => [
			{
				id: "interest_rate",
				variant: "primary" as const,
				data: rows.map((row) => ({
					x: row.calendar_year.toString(),
					y: (row.effective_interest_rate ?? 0) * 100,
				})),
			},
		],
		[rows],
	)

	const prepareTooltipValues = useMemo(
		() => (year: string) => {
			const yearData = rows.find(
				(row) => row.calendar_year.toString() === year,
			)
			if (!yearData) return []

			return [
				{
					id: "capital_at_risk",
					value: formatCurrency(
						yearData.capital_outstanding_start_of_year ?? 0,
						{ maximumFractionDigits: 0 },
					),
					variant: "primary" as const,
					colour: "#FFD900",
				},
				{
					id: "interest_rate",
					value: `${formatNumber(
						(yearData.effective_interest_rate ?? 0) * 100,
					)}%`,
					variant: "primary" as const,
					colour: "#149AE5",
				},
			]
		},
		[rows, formatCurrency, formatNumber],
	)

	const prepareTooltipLabel = useMemo(() => (year: string) => year, [])

	const CapitalAtRiskTickComponent = useMemo(
		() =>
			function CapitalAtRiskTick({
				formattedValue,
				...props
			}: TickRendererProps) {
				const value = parseFloat(formattedValue as string)

				let displayValue = ""
				if (value === 0) {
					displayValue = "0M"
				} else if (value >= 1000000) {
					displayValue = `${Math.round(value / 1000000)}M`
				} else if (value >= 1000) {
					displayValue = `${Math.round(value / 1000)}K`
				} else {
					displayValue = `${value}M`
				}

				return <Text {...props}>{displayValue}</Text>
			},
		[],
	)

	const YearTickComponent = useMemo(
		() =>
			function YearTick({ formattedValue, ...props }: TickRendererProps) {
				return <Text {...props}>{formattedValue}</Text>
			},
		[],
	)

	const RightAxisTickComponent = useMemo(
		() =>
			function RightAxisTick({
				formattedValue,
				...props
			}: TickRendererProps) {
				const value = parseFloat(formattedValue as string)
				if (value === 0) return null
				return <Text {...props}>{`${value.toFixed(2)}%`}</Text>
			},
		[],
	)

	return (
		<CardWrapper className="mb-4">
			<CardBody>
				<Heading as="h2" styleAs="h3" className="mb-6">
					{t("dashboard:statistics.financial_performance.title")}
				</Heading>
				<p className="mb-4 text-sm text-gray-600">
					{t("dashboard:statistics.platform.as_per_date", {
						date: DateTime.fromJSDate(
							endDateStatistics,
						).toLocaleString(),
					})}
				</p>
			</CardBody>
			<div className="h-96 w-full">
				{isLoading ? (
					<div className="flex size-full items-center justify-center">
						<div className="size-8 animate-spin rounded-full border-b-2 border-blue-600" />
					</div>
				) : (
					<MultiGraph
						barDatas={barData}
						lineDatas={lineData}
						lineYDomain={[0, 5]}
						prepareTooltipValues={prepareTooltipValues}
						prepareTooltipLabel={prepareTooltipLabel}
						xTickComponent={CapitalAtRiskTickComponent}
						yTickComponent={YearTickComponent}
						rightYTickComponent={RightAxisTickComponent}
					/>
				)}
			</div>
			<GraphLabels className="p-3 px-4 sm:p-5 sm:px-6">
				<GraphLabel
					type="bar"
					variant="primary"
					title={t("common:multi_graph.tooltip.capital_at_risk")}
				/>
				<GraphLabel
					type="line"
					variant="primary"
					title={t("common:multi_graph.tooltip.interest_rate")}
				/>
			</GraphLabels>
			<CardBody className="flex flex-col items-center gap-4 text-center">
				<p>
					{t(
						"dashboard:statistics.financial_performance.description",
					)}
				</p>
				<Link href={Routes.StatisticsDefaultRate}>
					{t("dashboard:statistics.default_rate.title", {
						start: defaultRates[0]?.year?.toString() ?? "",
						end:
							defaultRates[
								defaultRates.length - 1
							]?.year?.toString() ?? "",
					})}
				</Link>
			</CardBody>
		</CardWrapper>
	)
}
